import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../utils/Hooks";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { RequestStatus } from "../../utils/Enums";
import {
    deleteVendorContact,
    getCurrentVendorContact,
    getVendorContacts,
    resetDeleteVendorContactStatus,
} from "../../store/reducers/VendorContactsSlice";
import Breadcrumbs from "../common/Breadcrumbs";
import { LoggedInView } from "../common/LoggedInView";
import { BreadcrumbLink } from "../../models/BreadcrumbLink";

interface Replacement {
    startCountryId: number;
    destinationCountryId: number;
    replacementId: number | null;
    type: number;
}

interface DeleteVendorContactScreenProps {}

export const DeleteVendorContactScreen: React.FC<DeleteVendorContactScreenProps> = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const vendorContactsState = useAppSelector((state) => state.vendorContacts);
    const requestStatus: RequestStatus = vendorContactsState.deleteVendorContactStatus;
    const currentVendorContact = vendorContactsState.currentVendorContact;

    const { vendorId, contactId } = useParams();

    const parsedVendorId = vendorId ? parseInt(vendorId, 10) : undefined;
    const parsedContactId = contactId ? parseInt(contactId, 10) : undefined;

    const [replacements, setReplacements] = useState<{ [key: string]: Replacement }>({});

    const breadcrumbLinks: BreadcrumbLink[] = [
        { text: "Hankitjad", url: "/vendors" },
        { text: "Handkija kontaktid", url: `/vendors/${vendorId}/contacts` },
        { text: "Hankija kontakti kustutamine", url: `/vendors/${vendorId}/contacts/${contactId}/delete` },
    ];

    useEffect(() => {
        dispatch(resetDeleteVendorContactStatus());
    }, [location]);

    const handleConfirm = () => {
        if (parsedVendorId && parsedContactId) {
            dispatch(deleteVendorContact({ vendorId: parsedVendorId, contactId: parsedContactId, replacements }));
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    useEffect(() => {
        if (parsedVendorId && parsedContactId) {
            dispatch(getCurrentVendorContact({ vendorId: parsedVendorId, contactId: parsedContactId }));
            dispatch(getVendorContacts({ vendorId: parsedVendorId }));
        }
    }, [parsedVendorId, parsedContactId]);

    useEffect(() => {
        if (currentVendorContact && Array.isArray(currentVendorContact.routes)) {
            const defaultReplacements = currentVendorContact.routes.reduce((acc, route, index) => {
                const type = route.type === 2 ? "13.6 ldm" : "<13.6 ldm";
                acc[index] = {
                    startCountryId: route.startCountry.id,
                    destinationCountryId: route.destinationCountry.id,
                    replacementId: null,
                    type,
                };
                return acc;
            }, {} as { [key: string]: Replacement });
            setReplacements(defaultReplacements);
        }
    }, [currentVendorContact]);

    const handleReplacementChange = (routeIndex: number, event: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = event.target;
        setReplacements((prevReplacements) => ({
            ...prevReplacements,
            [routeIndex]: {
                ...prevReplacements[routeIndex],
                replacementId: parseInt(value),
            },
        }));
    };

    const showSuccess = () => (
        <div className="message msg-ok">
            <div>
                <h2>Hankija kontakt kustutatud</h2>
            </div>
        </div>
    );

    const typeText = (type:string) : string => {

        if ( type === '13.6 ldm' ) {
            return 'Grupp 2 (13,6 LDM)';
        }

        return 'Grupp 1 (0.1-13,5 LDM)'

    }

    const showConfirm = () => {
        if (!currentVendorContact || !Array.isArray(currentVendorContact.routes)) {
            return null;
        }

        return (
            <div className="message" style={{ textAlign: "center" }}>
                <div>
                    <h2>Oled kindel, et soovid hankija kontakti {' ' + currentVendorContact?.name} kustutada?</h2>
                    {currentVendorContact?.routes?.map((route, index) => (
                        <div key={index} style={{ border: 0 }}>
                            <p>
                                <strong>Marsruut {index + 1}:</strong> {route.startCountry.name} - {route.destinationCountry.name} {typeText(String(replacements[index]?.type))}
                            </p>
                            <select value={replacements[index]?.replacementId || ""} onChange={(event) => handleReplacementChange(index, event)}>
                                <option value="">Vali asendaja</option>
                                {vendorContactsState.vendorContacts.map((contact, contactIndex) => (
                                    <option key={contactIndex} value={contact.id}>
                                        {contact.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ))}
                    <p>
                        {requestStatus === RequestStatus.Failed && vendorContactsState.errors.length > 0 && (
                            <div className="message msg-error">
                                <div>
                                    <h2>Viga:</h2>
                                    <span>{vendorContactsState.errors[0]}<br/></span>
                                </div>
                            </div>
                        )}
                        <form method="post" id="delete_form" name="delete_form">
                            <input type="hidden" name="yes" value="yes"/>
                            <button type="button" onClick={handleConfirm}>
                                <span>
                                    <strong>Jah</strong>
                                </span>
                            </button>
                            <button type="button" onClick={handleCancel}>
                                Ei
                            </button>
                        </form>
                    </p>
                </div>
            </div>
        );
    };

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks}/>
            {requestStatus === RequestStatus.Success && showSuccess()}
            {requestStatus !== RequestStatus.Success && showConfirm()}
        </LoggedInView>
    );
};